import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import {
  fetchNotificationUsers,
  MOBILE_BACKEND_PUBLIC_BASE,
  sendPushNotification,
} from "../api/mobileBackendClient";

const SendNotification = () => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let active = true;

    const loadUsers = async () => {
      setLoadingUsers(true);
      setLoadError("");

      try {
        const response = await fetchNotificationUsers();
        if (active) {
          setUsers(Array.isArray(response?.users) ? response.users : []);
        }
      } catch (error) {
        if (active) {
          setLoadError(error.message || "Unable to load notification users.");
          setUsers([]);
        }
      } finally {
        if (active) {
          setLoadingUsers(false);
        }
      }
    };

    loadUsers();
    return () => {
      active = false;
    };
  }, []);

  const imagePreview = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : ""),
    [imageFile]
  );

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const resetForm = ({ clearResult = true, clearRecipients = false } = {}) => {
    setTitle("");
    setBody("");
    setImageFile(null);
    if (clearResult) {
      setResult(null);
    }
    if (clearRecipients) {
      setSelectedUsers([]);
    }
  };

  const handleSelectAll = () => {
    setSelectedUsers(users);
  };

  const handleSend = async () => {
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();

    if (!trimmedTitle) {
      toast.error("Notification title is required.");
      return;
    }

    if (!trimmedBody) {
      toast.error("Notification message is required.");
      return;
    }

    if (!selectedUsers.length) {
      toast.error("Select at least one user.");
      return;
    }

    setSubmitting(true);
    setResult(null);

    try {
      const response = await sendPushNotification({
        title: trimmedTitle,
        body: trimmedBody,
        userIds: selectedUsers.map((user) => user.user_id),
        imageFile,
      });

      setResult(response);
      toast.success(response.message || "Notification sent.");
      resetForm({ clearResult: false, clearRecipients: true });
    } catch (error) {
      const failurePayload = error?.payload;
      setResult(failurePayload || null);
      toast.error(error.message || "Unable to send notification.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
      <Card
        sx={{
          width: "100%",
          maxWidth: 980,
          borderRadius: 4,
          boxShadow: "0 14px 36px rgba(15, 23, 42, 0.12)",
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <NotificationsActiveIcon color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Send Push Notification
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Select users with active mobile tokens, add a message, and optionally attach a
                notification image.
              </Typography>
            </Box>
            <Chip
              label={`Image base: ${MOBILE_BACKEND_PUBLIC_BASE}`}
              variant="outlined"
              sx={{ maxWidth: "100%" }}
            />
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {loadError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {loadError}
            </Alert>
          ) : null}

          {result ? (
            <Alert severity={result.success ? "success" : "warning"} sx={{ mb: 3 }}>
              {result.message}
              {typeof result.sent_count === "number"
                ? ` Sent: ${result.sent_count}, Failed: ${result.failed_count || 0}, Skipped: ${
                    result.skipped_count || 0
                  }.`
                : ""}
            </Alert>
          ) : null}

          <Stack spacing={3}>
            <Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={1.5}
                sx={{ mb: 1.5 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Recipients
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<SelectAllIcon />}
                    onClick={handleSelectAll}
                    disabled={!users.length || loadingUsers || submitting}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="text"
                    startIcon={<RestartAltIcon />}
                    onClick={() => setSelectedUsers([])}
                    disabled={!selectedUsers.length || submitting}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={users}
                value={selectedUsers}
                onChange={(_, value) => setSelectedUsers(value)}
                loading={loadingUsers}
                getOptionLabel={(option) => option.display_name || `User #${option.user_id}`}
                isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Users"
                    placeholder={loadingUsers ? "Loading users..." : "Choose one or more users"}
                    helperText="Only users with an active mobile notification token are listed here."
                  />
                )}
              />
            </Box>

            <TextField
              label="Notification Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              fullWidth
            />

            <TextField
              label="Notification Message"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              fullWidth
              multiline
              minRows={4}
            />

            <Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={submitting}
                >
                  {imageFile ? "Change Image" : "Upload Image"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      setImageFile(file);
                    }}
                  />
                </Button>
                {imageFile ? (
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => setImageFile(null)}
                    disabled={submitting}
                  >
                    Remove Image
                  </Button>
                ) : null}
              </Stack>

              {imagePreview ? (
                <Box
                  sx={{
                    mt: 2,
                    width: 220,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #dbe2ea",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <img
                    src={imagePreview}
                    alt="Notification preview"
                    style={{ width: "100%", display: "block", objectFit: "cover" }}
                  />
                </Box>
              ) : null}
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => resetForm({ clearResult: true, clearRecipients: false })}
                disabled={submitting}
              >
                Reset Form
              </Button>
              <Button
                variant="contained"
                startIcon={
                  submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />
                }
                disabled={submitting || loadingUsers}
                onClick={handleSend}
              >
                {submitting ? "Sending..." : "Send Notification"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SendNotification;
